import React, {
  useState,
  SyntheticEvent,
  useEffect,
  ReactNode,
  cloneElement,
  useRef,
} from "react";
import {
  Autocomplete,
  TextField,
  Checkbox,
  Box,
  IconButton,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { getSearchData } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import { showPopupModal } from "@widgets/CommonModal";
import { StyleSettings } from "../../styles/StyleSettings";
import MultiPopup from "@widgets/commonpopup/MultiPopup";
import MenuMultiPopup from "@widgets/commonpopup/MenuMultiPopup";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import EmpPopup from "@widgets/commonpopup/EmpPopup";
import { showIssueModal } from "@widgets/IssueModal";
import { findSingleCodeRow } from "@shared/components/SnCodeBox";

/** 전용 팝업(MenuMultiPopup)으로 여는 cd_popup */
const MENU_MULTI_POPUP = "POPUP_MENU_MULTI";

/**
 * 도움창 결과 정렬.
 * 공통코드 도움창(popup_code.xml)처럼 no_seq_sort(표시순서)를 함께 내려주는 쪽은
 * 그 순서를 그대로 지키고, 품목 도움창(popup_item.xml)처럼 order by가 아예 없는
 * 쪽만 코드순으로 맞춘다.
 * 코드 비교는 numeric 옵션을 준다. (문자 비교만 하면 A10 이 A9 보다 앞선다)
 */
const sortForDisplay = (list: any[]) =>
  [...list].sort((a, b) => {
    const seq = Number(a?.no_seq_sort ?? 0) - Number(b?.no_seq_sort ?? 0);
    if (seq) return seq;
    return String(a?.code ?? "").localeCompare(String(b?.code ?? ""), "ko", {
      numeric: true,
    });
  });

/**
 * 명칭 [코드]. 드롭다운 첫 줄과 입력창에 쓴다. (예: 생산품 [M])
 * 코드가 없는 항목은 명칭만 그린다.
 */
const optionTitle = (option: any) => {
  const nm = option?.name ?? "";
  return option?.code ? `${nm} [${option.code}]` : nm;
};

/** 품목 도움창(popup_item.xml)만 내려주는 규격. 없으면 빈 문자열. */
const optionSpec = (option: any) => String(option?.dc_item_spec ?? "").trim();

/**
 * 검색 매칭용 전체 문자열.
 * 규격으로도 찾을 수 있어야 해서 붙여 둔다. 화면에는 이 문자열을 그대로 쓰지 않는다.
 * (규격 값 자체에 '/'가 들어 있어서 구분자로 이어 붙이면 눈으로 안 끊긴다)
 */
const optionLabel = (option: any) => {
  const spec = optionSpec(option);
  return spec ? `${optionTitle(option)} ${spec}` : optionTitle(option);
};

interface SnMultiCodeBoxProps {
  defaultValue?: any;
  code: string;
  cd_clas?: string;
  onValueChanged?: (value: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  popupConditions?: any;
}

const SnMultiCodeBox = React.memo(
  ({
    defaultValue,
    code,
    cd_clas,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    inputRef,
    isSearchPanel = false,
    onFocus,
    popupConditions = {},
  }: SnMultiCodeBoxProps) => {
    const { userInfo } = global();
    const [options, setOptions] = useState<any[]>([]);
    const [selected, setSelected] = useState<any[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [displayValue, setDisplayValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [borderWidth, setBorderWidth] = useState(isRequired ? "2px" : "1px");
    const [open, setOpen] = React.useState(false);

    // tab, enter, blur 이벤트 제어용
    const isOpenRef = useRef(false);

    /* 이름을 찾는 중에 값이 또 바뀌면 앞의 결과는 버린다 */
    const lookupKeyRef = useRef("");

    // 팝업 생성 시 필요한 state
    const [title, setTitle] = useState<string>("");
    const [width, setWidth] = useState<any>();
    const [height, setHeight] = useState<any>();
    const [autoSearch, setAutoSearch] = useState<boolean>(true);

    /**
     * 코드만 들어왔을 때(챗봇이 조회조건을 넣을 때 등) 이름을 찾아 보여 준다 (2026-10-14).
     * 팝업 · 직접 입력으로 고른 값은 이미 이름이 있어 다시 찾지 않는다 — 들어온 코드가 지금 보이는 것과 같으면 끝.
     * 찾기는 코드를 치고 Enter 를 누를 때와 같은 조회(findSingleCodeRow). 못 찾은 코드는 코드만 보여 준다
     */
    const fetchDefaultValue = async () => {
      try {
        const codes = String(defaultValue ?? "")
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean);
        if (codes.length === 0) return;
        const shown = selected.map((item: any) => String(item?.code ?? ""));
        if (codes.length === shown.length && codes.every((c) => shown.includes(c))) return;
        const key = codes.join(",");
        lookupKeyRef.current = key;
        const rows = await Promise.all(
          codes.map(async (c) => {
            const row = await findSingleCodeRow(code, c, cd_clas);
            return row && String(row.code) === c ? row : { code: c, name: "" };
          })
        );
        if (lookupKeyRef.current !== key) return;
        setOptions(rows);
        setSelected(rows);
        setDisplayValue(rows.map((item) => optionLabel(item)).join(", "));
      } catch (error: any) {}
    };

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      const multiPopupData = getSearchData(code, undefined);
      setTitle(multiPopupData.title);
      setAutoSearch(multiPopupData.autoSearch);
      setWidth(multiPopupData.width);
      setHeight(multiPopupData.height);
    }, [code]);

    useEffect(() => {
      if (!isFocused) {
        // 입력창은 한 줄이라 규격까지 넣으면 ellipsis 에 다 잘린다. 규격은 드롭다운에서만.
        setDisplayValue(selected.map((item) => optionTitle(item)).join(", "));
      }
    }, [isFocused]);

    const openPopup = async () => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      if (isOpenRef.current) return;

      isOpenRef.current = true;
      try {
        const mergedData = {
          ...popupConditions,
          str_search: inputValue,
          cd_clas: cd_clas || "",
        };
        let result;
        if (code === MENU_MULTI_POPUP) {
          // 목록을 전역 메뉴 스토어에서 읽는 전용 팝업 — 서버 조회가 없다
          result = await showPopupModal({
            title: title,
            children: (
              <MenuMultiPopup
                code={code}
                selectedData={mergedData}
                options={options}
              />
            ),
            width: width,
            height: height,
          });
        } else if (code === "POPUP_RECIPIENT" || code === "POPUP_REFERENCE") {
          const multiPopup = (
            <EmpPopup code={code} selectedData={mergedData} options={options} />
          );

          result = await showIssueModal({
            code: code,
            title: title,
            children: multiPopup,
            width: width,
            height: height,
          });
        } else {
          const multiPopup = (
            <MultiPopup
              code={code}
              selectedData={mergedData}
              options={options}
              autoSearch={autoSearch}
            />
          );

          result = await showPopupModal({
            title: title,
            children: multiPopup,
            width: width,
            height: height,
          });
        }
        const data = handleReturnFunction(result);
        if (data === "close") {
          // onFocus && onFocus("current");
          return;
        }
        if (data.length) {
          setOptions(data);
          setSelected(data);
          onValueChanged?.(data.map((item: any) => item.code).join(","));
          if (isSearchPanel) {
            setIsFocused(true);
            setOpen(true);
            onFocus && onFocus("current");
          } else {
            setIsFocused(false);
            onFocus && onFocus("next");
          }
        } else {
          // setClearValue(true);
          setOptions([]);
          setSelected([]);
          setDisplayValue("");
          onValueChanged?.("");
        }
      } finally {
        setTimeout(() => {
          isOpenRef.current = false;
        }, 100);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = async (key?: string) => {
      // 이미 팝업 열려있으면 return
      if (isOpenRef.current) return;

      if (!inputValue) {
        // 입력값이 없을 때
        if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
          await openPopup();
        } else {
          if (key === "Tab") {
            // setIsFocused(false);
            onFocus && onFocus("next");
          }
          setIsFocused(false);
        }
      } else {
        // 입력값이 있을 때
        if (key === "searchBtn") {
          await openPopup();
        } else if (options.length < 1) {
          const data = await checkData();
          if (data && data.length === 1) {
            setOptions(data);
            setSelected(data);
            onValueChanged?.(data.map((item: any) => item.code).join(","));
            setInputValue("");
            setOpen(false);
            setIsFocused(false);
            onFocus && onFocus("next");
          } else if (!isOpenRef.current) {
            setInputValue("");
            await openPopup();
          }
        } else {
          setInputValue("");
          setOpen(false);
          setIsFocused(false);
          onFocus && onFocus("next");
        }
      }
    };

    // 선택 변경 핸들러
    const handleSelectChange = (
      e: SyntheticEvent,
      value: any[],
      reason: any
    ) => {
      if (reason === "clear") {
        setOptions([]);
        setSelected([]);
        setDisplayValue("");
        onValueChanged?.("");
      }

      setSelected(value);
      onValueChanged?.(value.map((v) => v.code).join(","));
      setInputValue(inputValue); // 선택 후에도 검색어 유지
    };

    // 검색어 변경 핸들러
    const handleInputChange = (e: SyntheticEvent, value: string) => {
      setInputValue(value); // 검색어 업데이트
    };

    // 공백 상태에서 backspace 방지 핸들러
    const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Backspace" && !inputValue) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        await handleBlur(e.key);
      }
    };

    /**
     * code, like 검색으로 데이터 검증
     */
    const checkData = async () => {
      try {
        const popupSetting = PopupSettings[code];
        if (!popupSetting) return;
        // 직접 입력 검증쿼리(_02/_03)가 없는 팝업은 건너뛴다. 그대로 두면 blur 마다
        // 없는 쿼리를 두 번씩 불러 서버 오류만 쌓인다. (SnCodeBox.tsx:322 와 같은 처리)
        if (popupSetting.noLookup) return;

        const param1 = {
          id: code.toLowerCase() + "_02",
          params: {
            cd_firm: userInfo.cd_firm,
            [popupSetting.str_search]: inputValue,
            cd_clas: cd_clas || "",
          },
        };
        const result1 = await fnGetDataSet(param1);
        if (result1[0]?.length === 1) return handleReturnFunction(result1[0]);

        const param2 = {
          id: code.toLowerCase() + "_03",
          params: { cd_firm: userInfo.cd_firm, str_search: inputValue },
          cd_clas: cd_clas || "",
        };
        const result2 = await fnGetDataSet(param2);
        if (result2[0]?.length === 1) return handleReturnFunction(result2[0]);
      } catch (e) {}
    };

    /**
     * 팝업 데이터에 code, name 추가
     * @param dataArr 팝업 데이터
     */
    const handleReturnFunction = (data: any) => {
      const popupSetting = PopupSettings[code];
      if (!popupSetting || !data) return [];
      if (data === "close") {
        return data;
      } else {
        // 팝업·직접입력 결과가 모두 이 함수를 거치므로 여기서 한 번만 정렬한다.
        return sortForDisplay(
          data.map((d: any) => ({
            ...d,
            code: d[popupSetting.columns[0]],
            name: d[popupSetting.columns[1]],
          }))
        );
      }
    };

    // 팝업 아이콘 추가하는 함수
    const addSearchIcon = (endAdornment: ReactNode) => {
      // endAdornment가 React element가 아닌 경우 그대로 반환
      if (!React.isValidElement(endAdornment)) return endAdornment;

      // 드롭다운 화살표 버튼을 먼저 배치하고, 그 다음에 검색 버튼을 배치
      const children = React.Children.toArray(endAdornment.props.children);
      children.push(
        <IconButton
          key="search-button"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            handleBlur("searchBtn");
          }}
          sx={{
            width: 28,
            height: 28,
            marginLeft: "2px",
          }}
          edge="end"
          tabIndex={-1}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      );

      return cloneElement(endAdornment, {}, children);
    };

    return (
      <Box sx={{ position: "relative" }}>
        <Autocomplete
          // disablePortal
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          multiple
          disableCloseOnSelect
          options={options}
          value={selected}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={handleSelectChange}
          onFocus={handleFocus}
          onBlur={() => handleBlur()}
          clearOnBlur={false}
          disabled={isReadOnly}
          getOptionLabel={(option) => optionLabel(option)}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          filterOptions={(options, { inputValue }) =>
            inputValue
              ? options.filter((option) =>
                  // 명칭·코드 어느 쪽을 쳐도 걸리게 한다.
                  optionLabel(option)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              : options
          }
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              onKeyDown={handleKeyDown}
              slotProps={{
                input: {
                  ...params.InputProps,
                  inputRef: inputRef,
                  startAdornment:
                    !isFocused && displayValue ? (
                      <span
                        className="custom-adornment"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "calc(100% - 90px)",
                        }}
                      >
                        {displayValue}
                      </span>
                    ) : null,
                  endAdornment: addSearchIcon(params.InputProps.endAdornment),
                  style: {
                    height: StyleSettings.control_height,
                    fontSize: StyleSettings.font,
                  },
                },
              }}
            />
          )}
          renderOption={(props, option, { selected }) => {
            // 규격은 둘째 줄에 작게 깐다. 한 줄로 이으면 규격 안의 '/' 와 섞여
            // 어디부터 규격인지 눈으로 끊기지 않는다.
            const spec = optionSpec(option);
            return (
              // 드롭다운 한 줄은 SnComboBox 기준. 맨 li 는 MUI 기본 body1(16px)을 받고
              // 체크박스 기본 padding 9px 이 붙어 단일 콤보보다 크게 보인다.
              <MenuItem
                {...props}
                key={option.code}
                sx={{ fontSize: StyleSettings.font, minHeight: "32px" }}
              >
                <Checkbox checked={selected} size="small" sx={{ p: 0, mr: 1 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: StyleSettings.font,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {optionTitle(option)}
                  </Box>
                  {spec && (
                    <Box
                      // 잘리면 hover 로 전체를 볼 수 있게 title 을 같이 건다.
                      title={spec}
                      sx={{
                        fontSize: 11,
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {spec}
                    </Box>
                  )}
                </Box>
              </MenuItem>
            );
          }}
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              padding: 0,
              backgroundColor: "white",
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
            "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
              padding: "0",
              paddingLeft: "14px",
              fontSize: StyleSettings.font,
            },
            "& .custom-adornment": {
              // focus 시, dropdown 보이는 style
              position: "absolute",
              left: "14px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "calc(100% - 60px)",
              pointerEvents: "none",
            },
          }}
        />
      </Box>
    );
  }
);
export default SnMultiCodeBox;
